import { Component, OnInit, ChangeDetectionStrategy, Inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Configuration, TFMService } from 'app/mock-api/tfm.service';
import { TFMCartService } from 'app/mock-api/tfm-cart.service';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'invoice-view-dialog',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule, MatFormFieldModule, MatSelectModule, MatDialogModule],
  providers: [TFMService],
  templateUrl: './invoice-view-dialog.component.html',
  styleUrl: './invoice-view-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Default
})
export class InvoiceViewDialogComponent implements OnInit {

  public view: any = {};
  /** Raw URL for fetching invoice HTML (same as iframe src). */
  quoteViewUrlString = '';
  /** Invoice HTML with service description injected in the red box; when set, we show this instead of iframe. */
  invoiceHtml: ReturnType<DomSanitizer['bypassSecurityTrustHtml']> | null = null;
  loading = false;

  constructor(
    private _tfmService: TFMService,
    private _tfmCartService: TFMCartService,
    private _config: Configuration,
    public dialogRef: MatDialogRef<InvoiceViewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {
    this.view.type = data.type;
    this.view.search = data.search;
    this.view.config = data.config;
    this.view.itin = data.itin;
    this.selectedBrand = this.view.itin?.content?.itinerary?.siteId ?? 0;
    this.setLinks(0);
  }

  private setLinks(value: number) {
    const siteId = this.view.itin?.content?.itinerary?.siteId;
    const guid = this.view.itin?.content?.itinerary?.guid;
    if (!siteId || !guid) return;
    this.quoteViewUrlString = `${this._config.TFMApiLink.href}v1/sites/${siteId}/itineraries/${guid}/documents/pdf/invoice/ss`;
    let urlDownload = `${this._config.TFMApiLink.href}v1/sites/${siteId}/itineraries/${guid}/documents/pdf/invoice`;
    if (value > 0) {
      this.quoteViewUrlString += `?sid=${value}`;
      urlDownload += `?sid=${value}`;
    }
    this.view.quoteViewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.quoteViewUrlString);
    this.view.quoteDownloadUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlDownload);
  }

  selectedBrand = 0;

  onBrandChange(value: number) {
    this.setLinks(value);
    this.invoiceHtml = null;
    this.loadInvoiceWithDescription();
  }

  ngOnInit(): void {
    this.ensureItemsThenLoadInvoice();
  }

  /**
   * If items are missing, load them via API; then fetch invoice and inject description into red box.
   */
  private ensureItemsThenLoadInvoice(): void {
    const itin = this.view.itin;
    let items = itin?.items?.content ?? itin?.content?.items?.content;
    const links = itin?.content?.itinerary?._links;
    if ((!items || items.length === 0) && links) {
      this.loading = true;
      this._tfmService.filterAndSend(links, 'items').subscribe({
        next: (res) => {
          if (!this.view.itin.items) this.view.itin.items = { content: [] };
          this.view.itin.items.content = res?.content ?? [];
          this.loadInvoiceWithDescription();
        },
        error: () => {
          this.loading = false;
          this.invoiceHtml = null;
        }
      });
      return;
    }
    this.loadInvoiceWithDescription();
  }

  /**
   * Fetch invoice HTML and inject only the service description into the first Service cell (red box).
   */
  private loadInvoiceWithDescription(): void {
    const itin = this.view.itin;
    const items = itin?.items?.content ?? itin?.content?.items?.content;
    const firstItem = Array.isArray(items) && items.length > 0 ? items[0] : null;
    const description = this.getServiceDescription(firstItem);

    if (!description) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.invoiceHtml = null;
    this.http.get(this.quoteViewUrlString, { responseType: 'text', withCredentials: true }).subscribe({
      next: (html) => {
        this.loading = false;
        if (description) {
          const modified = this.injectDescriptionIntoFirstServiceCell(html, description);
          if (modified) {
            this.invoiceHtml = this.sanitizer.bypassSecurityTrustHtml(modified);
          }
        }
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private getServiceDescription(item: any): string {
    if (!item) return '';
    const raw = item?.serviceDescription ?? item?.description ?? item?.item?.serviceDescription ?? item?.item?.description;
    return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : '';
  }

  /**
   * Find the first Service table (thead contains "Service"), then the first data row's first td,
   * and inject the description in the red-box area (right side of that cell).
   */
  private injectDescriptionIntoFirstServiceCell(html: string, description: string): string | null {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const tables = doc.querySelectorAll('table');
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i];
        const ths = table.querySelectorAll('thead th');
        let hasService = false;
        ths.forEach((th) => {
          if (th.textContent?.trim().toLowerCase() === 'service' || th.textContent?.trim().toLowerCase() === 'beskrying') {
            hasService = true;
          }
        });
        if (!hasService) continue;

        const tbody = table.querySelector('tbody');
        if (!tbody) continue;
        const rows = Array.from(tbody.querySelectorAll('tr'));
        for (const row of rows) {
          const cells = row.querySelectorAll('td');
          if (cells.length < 2) continue;
          const firstTd = cells[0];
          const text = (firstTd.textContent || '').trim();
          if (text === '' || text.toLowerCase().includes('includes') || (text.includes(' - ') && text.match(/\d{1,2}\s+\w{3}\s+\d{4}/))) {
            continue;
          }
          const escaped = description
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
          firstTd.innerHTML = `<div style="display:flex;width:100%;align-items:flex-start;gap:12px"><div style="flex:0 0 auto">${firstTd.innerHTML}</div><div style="flex:1 1 auto;min-width:0;font-size:14px;line-height:1.4">${escaped}</div></div>`;
          return doc.documentElement.outerHTML;
        }
        return null;
      }
      return null;
    } catch {
      return null;
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
